import type { CSSProperties, FC, ReactNode } from 'react'
import type { SolutionVisual as SolutionVisualType } from '@/constants/solutions'

import Image from 'next/image'
import { useRef } from 'react'
import {
    ArrowRight,
    Check,
    FileText,
    LoaderCircle,
    Search,
    Sparkles
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

interface VisualProps {
    inView: boolean
    image: string
    imageAlt: string
}

interface SolutionVisualProps {
    type: SolutionVisualType
    image: string
    imageAlt: string
    className?: string
}

const ENTER = 'transition-[opacity,transform] duration-700 ease-expo'

const enter = (inView: boolean, delay: number): { className: string, style: CSSProperties } => ({
    className: cn(ENTER, inView ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'),
    style: { transitionDelay: inView ? `${delay}ms` : '0ms' }
})

interface FrameProps {
    title: string
    status?: string
    children: ReactNode
    className?: string
}

const Frame: FC<FrameProps> = ({ title, status = 'Live', children, className }): ReactNode => {
    return (
        <div className={cn('border border-line-strong bg-surface/95 backdrop-blur-md', className)}>
            <div className='flex items-center justify-between gap-4 border-b border-line px-4 py-2.5'>
                <span className='type-label truncate text-muted-foreground'>
                    {title}
                </span>

                <span className='type-label flex shrink-0 items-center gap-2 text-ember'>
                    <span className='size-1.5 rounded-full bg-ember' />
                    {status}
                </span>
            </div>

            {children}
        </div>
    )
}

interface PhotoProps {
    image: string
    imageAlt: string
    className?: string
}

const Photo: FC<PhotoProps> = ({ image, imageAlt, className }): ReactNode => {
    return (
        <>
            <Image
                src={image}
                alt={imageAlt}
                fill
                sizes='(min-width: 1024px) 40vw, 100vw'
                className={cn('img-tone object-cover', className)}
            />

            <div className='absolute inset-0 bg-gradient-to-t from-background/85 via-background/30 to-background/10' />
        </>
    )
}

/* 01 AI Agents: a roster of agents at work */
const AgentsVisual: FC<VisualProps> = ({ inView, image, imageAlt }): ReactNode => {
    const agents = [
        { name: 'Intake agent', task: 'Triaging 38 new requests', status: 'Running', progress: 72 },
        { name: 'Research agent', task: 'Summarizing 12 supplier documents', status: 'Running', progress: 46 },
        { name: 'Operations agent', task: 'Updating 214 CRM records', status: 'Review', progress: 91 }
    ]

    return (
        <>
            <Photo image={image} imageAlt={imageAlt} />

            <div className='absolute inset-x-4 bottom-4 flex flex-col gap-2 md:inset-x-6 md:bottom-6'>
                {agents.map((agent, index) => {
                    const motion = enter(inView, 150 + index * 160)

                    return (
                        <div
                            key={agent.name}
                            className={cn('border border-line-strong bg-background/80 p-4 backdrop-blur-md', motion.className)}
                            style={motion.style}
                        >
                            <div className='flex items-center justify-between gap-4'>
                                <div className='flex items-center gap-3'>
                                    <span className='flex size-8 items-center justify-center border border-line-strong'>
                                        <Sparkles className='size-3.5 text-ember' />
                                    </span>

                                    <div>
                                        <p className='text-sm text-foreground'>
                                            {agent.name}
                                        </p>

                                        <p className='text-xs text-muted-foreground'>
                                            {agent.task}
                                        </p>
                                    </div>
                                </div>

                                <span
                                    className={cn(
                                        'type-label border px-2 py-1',
                                        agent.status === 'Review' ? 'border-ember/50 text-ember' : 'border-line-strong text-soft'
                                    )}
                                >
                                    {agent.status}
                                </span>
                            </div>

                            <div className='mt-3 h-px w-full bg-line'>
                                <div
                                    className='h-px bg-ember transition-[width] duration-[1600ms] ease-expo'
                                    style={{ width: inView ? `${agent.progress}%` : '0%', transitionDelay: `${400 + index * 160}ms` }}
                                />
                            </div>
                        </div>
                    )
                })}
            </div>
        </>
    )
}

/* 02 Digital Workflows: a flow that runs itself */
interface FlowNodeProps {
    inView: boolean
    label: string
    kind: string
    delay: number
    highlight?: boolean
}

const FlowNode: FC<FlowNodeProps> = ({ inView, label, kind, delay, highlight }): ReactNode => {
    const motion = enter(inView, delay)

    return (
        <div
            className={cn(
                'flex items-center justify-between gap-3 border px-3 py-2.5',
                highlight ? 'border-ember/50 bg-ember/[0.08]' : 'border-line-strong bg-background/70',
                motion.className
            )}
            style={motion.style}
        >
            <span className='text-[13px] text-foreground'>
                {label}
            </span>

            <span className='type-label text-muted-foreground'>
                {kind}
            </span>
        </div>
    )
}

interface FlowConnectorProps {
    inView: boolean
    delay: number
}

const FlowConnector: FC<FlowConnectorProps> = ({ inView, delay }): ReactNode => {
    return (
        <div className='relative mx-auto h-7 w-px overflow-hidden bg-line-strong' aria-hidden='true'>
            <span
                className={cn('flow-down absolute inset-x-0 top-0 h-3 bg-ember transition-opacity duration-500', inView ? 'opacity-100' : 'opacity-0')}
                style={{ animationDelay: `${delay}ms` }}
            />
        </div>
    )
}

const WorkflowVisual: FC<VisualProps> = ({ inView }): ReactNode => {
    return (
        <div className='bg-dots absolute inset-0 flex items-center justify-center p-5 md:p-8'>
            <div className='w-full max-w-[380px]'>
                <FlowNode inView={inView} label='Contract signed in CRM' kind='Trigger' delay={100} highlight />
                <FlowConnector inView={inView} delay={0} />

                <div className='grid grid-cols-2 gap-2'>
                    <FlowNode inView={inView} label='Create customer in ERP' kind='Action' delay={260} />
                    <FlowNode inView={inView} label='Provision workspace' kind='Action' delay={340} />
                </div>

                <FlowConnector inView={inView} delay={400} />
                <FlowNode inView={inView} label='Finance approval' kind='Human' delay={480} />
                <FlowConnector inView={inView} delay={800} />
                <FlowNode inView={inView} label='Kickoff email and tasks' kind='Action' delay={620} />

                <div
                    className={cn('mt-4 flex items-center justify-between border-t border-line pt-3 font-mono text-[11px] text-muted-foreground', enter(inView, 800).className)}
                    style={enter(inView, 800).style}
                >
                    <span>Run #4,218</span>
                    <span className='text-ember'>Completed in 3m 41s</span>
                </div>
            </div>
        </div>
    )
}

/* 03 AI Automation: documents read and understood */
const AutomationVisual: FC<VisualProps> = ({ inView, image, imageAlt }): ReactNode => {
    const fields = [
        { label: 'Supplier', value: 'Fieldstone Materials' },
        { label: 'Invoice total', value: '$18,240.00' },
        { label: 'Due date', value: '14 Nov 2026' },
        { label: 'PO match', value: 'PO-7731' }
    ]

    return (
        <>
            <Photo image={image} imageAlt={imageAlt} />

            <div className='absolute inset-x-4 bottom-4 md:inset-x-6 md:bottom-6'>
                <Frame title='document understanding' status='98% confidence' className={enter(inView, 150).className}>
                    <div className='flex items-center gap-3 border-b border-line px-4 py-3'>
                        <FileText className='size-4 text-soft' />
                        <span className='font-mono text-[12px] text-foreground'>invoice_fieldstone_0412.pdf</span>
                    </div>

                    <ul className='divide-y divide-line'>
                        {fields.map((field, index) => {
                            const motion = enter(inView, 350 + index * 140)

                            return (
                                <li
                                    key={field.label}
                                    className={cn('flex items-center justify-between gap-4 px-4 py-2.5', motion.className)}
                                    style={motion.style}
                                >
                                    <span className='text-xs text-muted-foreground'>
                                        {field.label}
                                    </span>

                                    <span className='flex items-center gap-2 text-[13px] text-foreground'>
                                        {field.value}
                                        <Check className='size-3.5 text-ember' />
                                    </span>
                                </li>
                            )
                        })}
                    </ul>
                </Frame>
            </div>
        </>
    )
}

/* 04 Business Process Automation: before and after */
const ProcessVisual: FC<VisualProps> = ({ inView }): ReactNode => {
    const before = [
        'Order arrives by email',
        'Re-keyed into ERP',
        'Credit check by hand',
        'Finance approval by email',
        'Invoice created manually',
        'Payment chased by phone'
    ]

    const after = [
        'Order captured and validated',
        'Credit and pricing checked automatically',
        'Invoice issued and reconciled'
    ]

    return (
        <div className='absolute inset-0 grid grid-cols-2 gap-px bg-line'>
            <div className='flex flex-col bg-surface p-4 md:p-6'>
                <div className='flex items-center justify-between'>
                    <span className='type-label text-muted-foreground'>Before</span>
                    <span className='font-mono text-[11px] text-muted-foreground'>3 days</span>
                </div>

                <ol className='mt-5 flex flex-col gap-1.5'>
                    {before.map((step, index) => {
                        const motion = enter(inView, 100 + index * 90)

                        return (
                            <li
                                key={step}
                                className={cn('border border-line px-2.5 py-2 text-[12px] text-muted-foreground line-through decoration-white/20', motion.className)}
                                style={motion.style}
                            >
                                {step}
                            </li>
                        )
                    })}
                </ol>
            </div>

            <div className='flex flex-col bg-background p-4 md:p-6'>
                <div className='flex items-center justify-between'>
                    <span className='type-label text-ember'>With Cortex</span>
                    <span className='font-mono text-[11px] text-ember'>4 min</span>
                </div>

                <ol className='mt-5 flex flex-col gap-1.5'>
                    {after.map((step, index) => {
                        const motion = enter(inView, 700 + index * 160)

                        return (
                            <li
                                key={step}
                                className={cn('flex items-center gap-2 border border-ember/40 bg-ember/[0.07] px-2.5 py-2 text-[12px] text-foreground', motion.className)}
                                style={motion.style}
                            >
                                <Check className='size-3.5 shrink-0 text-ember' />
                                {step}
                            </li>
                        )
                    })}
                </ol>

                <div
                    className={cn('mt-auto border-t border-line pt-4', enter(inView, 1300).className)}
                    style={enter(inView, 1300).style}
                >
                    <p className='font-heading text-3xl tracking-[-0.04em] text-foreground md:text-4xl'>-60%</p>
                    <p className='text-xs text-muted-foreground'>Cost per order processed</p>
                </div>
            </div>
        </div>
    )
}

/* 05 Customer Support Automation: a resolved conversation */
const SupportVisual: FC<VisualProps> = ({ inView }): ReactNode => {
    const customer = enter(inView, 150)
    const typing = inView
    const reply = enter(inView, 1300)
    const resolved = enter(inView, 1900)

    return (
        <div className='bg-dots absolute inset-0 flex items-center justify-center p-4 md:p-8'>
            <Frame title='support / web chat' status='Online' className='w-full max-w-[420px]'>
                <div className='flex min-h-[300px] flex-col gap-3 p-4'>
                    <div className={cn('ml-auto max-w-[80%] bg-foreground px-3.5 py-2.5 text-[13px] text-background', customer.className)} style={customer.style}>
                        Hi, can I move my delivery for order #20931 to Friday morning?
                    </div>

                    <div className='relative min-h-[64px]'>
                        <div
                            className={cn(
                                'absolute top-0 left-0 flex items-center gap-1 border border-line-strong px-3 py-3 transition-opacity duration-300',
                                typing ? 'typing-out opacity-100' : 'opacity-0'
                            )}
                            aria-hidden='true'
                            style={{ transitionDelay: typing ? '500ms' : '0ms' }}
                        >
                            <span className='size-1.5 animate-pulse rounded-full bg-soft' />
                            <span className='size-1.5 animate-pulse rounded-full bg-soft [animation-delay:150ms]' />
                            <span className='size-1.5 animate-pulse rounded-full bg-soft [animation-delay:300ms]' />
                        </div>

                        <div className={cn('relative max-w-[85%] border border-line-strong bg-surface-2 px-3.5 py-2.5 text-[13px] text-foreground', reply.className)} style={reply.style}>
                            Done. Order #20931 now arrives Friday between 9:00 and 12:00, and a confirmation is on its way to your inbox. Anything else?
                        </div>
                    </div>

                    <div className={cn('mt-auto flex flex-wrap items-center gap-2 border-t border-line pt-3', resolved.className)} style={resolved.style}>
                        <span className='type-label border border-ember/50 px-2 py-1 text-ember'>Resolved automatically</span>
                        <span className='type-label border border-line-strong px-2 py-1 text-soft'>42s</span>
                        <span className='type-label border border-line-strong px-2 py-1 text-soft'>CSAT 5/5</span>
                    </div>
                </div>
            </Frame>
        </div>
    )
}

/* 06 Sales Automation: a pipeline that keeps itself current */
const SalesVisual: FC<VisualProps> = ({ inView }): ReactNode => {
    const columns = [
        {
            title: 'New',
            leads: [
                { company: 'Fieldstone', score: 64 },
                { company: 'Orbit Labs', score: 58 }
            ]
        },
        {
            title: 'Qualified',
            leads: [
                { company: 'Lumen Bank', score: 92, hot: true },
                { company: 'Pine & Co', score: 81 }
            ]
        },
        {
            title: 'Meeting',
            leads: [
                { company: 'Arden Group', score: 88 }
            ]
        }
    ]

    return (
        <div className='absolute inset-0 flex flex-col p-4 md:p-6'>
            <div className='flex items-center justify-between border-b border-line pb-3'>
                <span className='type-label text-muted-foreground'>Pipeline, this week</span>
                <span className='type-label text-ember'>+34 qualified</span>
            </div>

            <div className='mt-4 grid flex-1 grid-cols-3 gap-2'>
                {columns.map((column, columnIndex) => (
                    <div key={column.title} className='flex flex-col gap-2 border border-line bg-surface/60 p-2'>
                        <span className='type-label px-1 pt-1 text-muted-foreground'>
                            {column.title}
                        </span>

                        {column.leads.map((lead, leadIndex) => {
                            const motion = enter(inView, 150 + columnIndex * 200 + leadIndex * 120)

                            return (
                                <div
                                    key={lead.company}
                                    className={cn(
                                        'flex flex-col gap-2 border p-2.5',
                                        'hot' in lead && lead.hot ? 'border-ember/50 bg-ember/[0.08]' : 'border-line-strong bg-background',
                                        motion.className
                                    )}
                                    style={motion.style}
                                >
                                    <span className='truncate text-[12px] text-foreground'>
                                        {lead.company}
                                    </span>

                                    <div className='flex items-center justify-between gap-1'>
                                        <span className='font-mono text-[10px] text-muted-foreground'>Score</span>
                                        <span className={cn('font-mono text-[11px]', lead.score > 85 ? 'text-ember' : 'text-soft')}>
                                            {lead.score}
                                        </span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ))}
            </div>

            <div
                className={cn('mt-3 flex items-center gap-2 border border-line-strong bg-background/80 px-3 py-2.5 text-[12px] text-soft', enter(inView, 1100).className)}
                style={enter(inView, 1100).style}
            >
                <Sparkles className='size-3.5 shrink-0 text-ember' />
                Follow-up drafted for Lumen Bank, meeting notes synced to CRM
            </div>
        </div>
    )
}

/* 07 Internal Operations: month-end close, handled */
const OperationsVisual: FC<VisualProps> = ({ inView, image, imageAlt }): ReactNode => {
    const tasks = [
        { label: 'Match 1,204 supplier invoices', done: true },
        { label: 'Flag 7 exceptions for review', done: true },
        { label: 'Sync expenses to the ledger', done: true },
        { label: 'Generate month-end close report', done: false }
    ]

    return (
        <>
            <Photo image={image} imageAlt={imageAlt} className='img-mono' />

            <div className='absolute inset-x-4 bottom-4 md:inset-x-6 md:bottom-6'>
                <Frame title='finance / month-end close' status='3 of 4 done' className={enter(inView, 150).className}>
                    <ul className='divide-y divide-line'>
                        {tasks.map((task, index) => {
                            const motion = enter(inView, 300 + index * 220)

                            return (
                                <li
                                    key={task.label}
                                    className={cn('flex items-center gap-3 px-4 py-3 text-[13px]', motion.className)}
                                    style={motion.style}
                                >
                                    <span
                                        className={cn(
                                            'flex size-5 shrink-0 items-center justify-center border',
                                            task.done ? 'border-ember/60 bg-ember/15 text-ember' : 'border-line-strong text-soft'
                                        )}
                                    >
                                        {task.done
                                            ? <Check className='size-3' />
                                            : <LoaderCircle className='size-3 animate-spin' />}
                                    </span>

                                    <span className={task.done ? 'text-foreground' : 'text-soft'}>
                                        {task.label}
                                    </span>
                                </li>
                            )
                        })}
                    </ul>
                </Frame>
            </div>
        </>
    )
}

/* 08 Research and Knowledge Systems: cited answers */
const KnowledgeVisual: FC<VisualProps> = ({ inView }): ReactNode => {
    const question = 'What is our refund policy for enterprise contracts?'
    const answer = enter(inView, 900)

    return (
        <div className='bg-dots absolute inset-0 flex items-center justify-center p-4 md:p-8'>
            <div className='w-full max-w-[440px]'>
                <div className='flex items-center gap-3 border border-line-strong bg-background px-4 py-3'>
                    <Search className='size-4 shrink-0 text-soft' />

                    <span className='relative block min-w-0 flex-1 overflow-hidden text-[13px] whitespace-nowrap text-foreground'>
                        <span
                            className='block overflow-hidden transition-[max-width] duration-[1400ms] ease-[steps(52)]'
                            style={{ maxWidth: inView ? '100%' : '0%' }}
                        >
                            {question}
                        </span>
                    </span>

                    <ArrowRight className='size-4 shrink-0 text-ember' />
                </div>

                <div className={cn('mt-2 border border-line-strong bg-surface p-4', answer.className)} style={answer.style}>
                    <p className='type-label flex items-center gap-2 text-ember'>
                        <Sparkles className='size-3.5' />
                        Answer
                    </p>

                    <p className='mt-3 text-[13px] leading-relaxed text-foreground'>
                        Enterprise customers can request a pro-rated refund within 30 days of renewal
                        <sup className='ml-0.5 text-ember'>1</sup>
                        . Refunds above $25,000 need approval from the finance director
                        <sup className='ml-0.5 text-ember'>2</sup>
                        .
                    </p>

                    <div className='mt-4 flex flex-col gap-1.5 border-t border-line pt-3'>
                        {['Enterprise MSA v4, section 12.3', 'Finance approvals policy, 2026'].map((source, index) => {
                            const motion = enter(inView, 1200 + index * 150)

                            return (
                                <span
                                    key={source}
                                    className={cn('flex items-center gap-2 font-mono text-[11px] text-muted-foreground', motion.className)}
                                    style={motion.style}
                                >
                                    <span className='text-ember'>[{index + 1}]</span>
                                    {source}
                                </span>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}

const VISUALS: Record<SolutionVisualType, FC<VisualProps>> = {
    agents: AgentsVisual,
    workflow: WorkflowVisual,
    automation: AutomationVisual,
    process: ProcessVisual,
    support: SupportVisual,
    sales: SalesVisual,
    operations: OperationsVisual,
    knowledge: KnowledgeVisual
}

export const SolutionVisual: FC<SolutionVisualProps> = ({ type, image, imageAlt, className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref)
    const Visual = VISUALS[type]

    return (
        <div
            ref={ref}
            className={cn('relative aspect-[4/5] min-h-[420px] overflow-hidden border border-line bg-surface sm:aspect-[5/4] md:aspect-[4/5]', className)}
        >
            <Visual inView={inView} image={image} imageAlt={imageAlt} />
        </div>
    )
}
